import { useFrame } from '@react-three/fiber';
import gsap from 'gsap';
import {
  type FC,
  type MutableRefObject,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

interface DummyProps {
  onMount: () => void;
}

const Dummy: FC<DummyProps> = ({ onMount }) => {
  useEffect(() => {
    onMount();
  }, [onMount]);
  return null;
};

export interface Animatable {
  animationProgress: number;
}

export interface AnimationControllerProps {
  children: ReactNode;
  animatableRef: MutableRefObject<Animatable>;
  getIsPresent: () => boolean;
}

const AnimationController: FC<AnimationControllerProps> = ({
  children,
  animatableRef,
  getIsPresent,
}) => {
  const localIsPresent = useRef<boolean>(getIsPresent());
  const [isActive, setIsActive] = useState<boolean>(getIsPresent());

  const fireAnimation = (direction: 'FW' | 'BW') => {
    const intro = direction === 'FW';
    return gsap.timeline().to(animatableRef.current, {
      animationProgress: intro ? 1 : 0,
      duration: 1,
      ease: intro ? 'power1.out' : 'power1.in',
    });
  };

  useFrame(() => {
    const getIsPresentResult = getIsPresent();
    if (localIsPresent.current === getIsPresentResult) return;
    localIsPresent.current = getIsPresentResult;

    if (getIsPresentResult) {
      setIsActive(true);
      return;
    }

    fireAnimation('BW').call(() => setIsActive(false));
  });

  return isActive ? (
    <>
      {children}
      <Dummy onMount={() => fireAnimation('FW')} />
    </>
  ) : null;
};

AnimationController.displayName = 'AnimationController';
export default AnimationController;
